import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useRef } from "react";
import { annotationTransformStyle } from "../annotation-transform";
import { resolveDrawingFrame } from "./drawing-transform";
import { strokeToSvgPath } from "./drawing-stroke-path";
import { useAnnotationHoverStore } from "@/data/annotation-hover-store";
/**
 * Reference viewBox dimensions used to size strokes into pixel-
 * equivalent units. Mirrors the panel's natural dimensions
 * (`$expand-natural-width / $expand-natural-height` in
 * `block-detail-modal.scss`). With the SVG sized to
 * `width × REFERENCE_W` viewBox units, a stroke at
 * `STROKE_RENDER_SIZE = 10` user units renders as ~10 screen pixels
 * at full panel size — matching the live capture canvas at draw
 * time and the legacy full-bleed renderer.
 */
const REFERENCE_VIEWBOX_W = 460;
const REFERENCE_VIEWBOX_H = 346;
const STROKE_RENDER_SIZE = 10;
/**
 * Screen-pixel hit tolerance around the visible polygon's outline.
 * The path's transparent stroke runs a band of this many pixels on
 * each side, so the user doesn't need to land exactly on the
 * (often-thin) painted polygon. Figma's vector frame uses ~3px on
 * each side; matching at 6px total / 3px-per-edge here.
 *
 * Held constant in screen pixels via two layers:
 *
 *   1. `vector-effect: non-scaling-stroke` neutralizes the SVG's
 *      internal viewBox→host zoom, so the stroke width stays a
 *      constant SVG-final pixel value regardless of natural-vs-current
 *      sizing.
 *   2. The wrapper now carries a CSS `transform: scale(frame.width,
 *      frame.height)` that the SVG's `non-scaling-stroke` does NOT
 *      see (it only fights SVG-internal transforms, not parent CSS).
 *      We compensate by pre-multiplying `strokeWidth` by `1 /
 *      min(scaleX, scaleY)` — see `compensatedHitStrokeWidth` below.
 *
 * Without (2), a drawing scaled to 50% would have its hit tolerance
 * silently halved to 3px (perpendicular component of the parent
 * scale), which makes thin drawings effectively unclickable.
 */
const STROKE_HIT_PADDING_PX = 6;
// Floor on the per-axis scale before reciprocating so a degenerate
// (collapsed-to-zero) drawing doesn't push the compensation factor
// to `Infinity`. Mirrors `SCALE_EPSILON` in `annotation-transform.ts`.
const FRAME_AXIS_EPSILON = 0.0001;
function compensatedHitStrokeWidth(scaleX, scaleY) {
    // Stroke thickness perpendicular to the path direction varies with
    // the parent CSS scale. For a path running near-horizontal, the
    // perpendicular is the Y axis; near-vertical paths take the X axis.
    // Using `min(scaleX, scaleY)` guarantees the band is AT LEAST 6
    // screen px on whichever axis would otherwise shrink it the most —
    // the other axis gets a touch more tolerance, which is fine
    // (over-targets are easier to click than under-targets).
    const minScale = Math.max(Math.min(Math.abs(scaleX), Math.abs(scaleY)), FRAME_AXIS_EPSILON);
    return STROKE_HIT_PADDING_PX / minScale;
}
export const DrawingAnnotationView = memo(function DrawingAnnotationView({ annotation, mode, drawingSelection, }) {
    const selection = drawingSelection;
    // Resolve to canonical (x, y, width, height, rotation, strokes)
    // regardless of whether the row is canonical or legacy. Memoized
    // on the row identity so a parent re-render that only touches
    // an unrelated row doesn't recompute the legacy bbox.
    const frame = useMemo(() => resolveDrawingFrame(annotation), [annotation]);
    const moveStartRef = useRef(null);
    // Tracks whether the gesture actually moved before pointer-up.
    // A click-to-select with no drag has no changes to persist, and
    // firing a settle PATCH for it would race against any imminent
    // delete (the wasteful PATCH 404s after the row is removed,
    // which used to surface a "Failed to save drawing position"
    // toast and even resurrect the row).
    const moveDirtyRef = useRef(false);
    // Build SVG path strings against a viewBox sized in the drawing's
    // NATURAL pixel-equivalent units (frozen at commit). The wrapper
    // sizes itself to the CURRENT bbox, so a resize changes the
    // viewBox→host scale and the strokes scale photographically with
    // it. At commit (when current = natural) the stroke renders at
    // the same ~10px weight the user saw on the canvas.
    const viewBoxW = Math.max(frame.naturalWidth * REFERENCE_VIEWBOX_W, 1);
    const viewBoxH = Math.max(frame.naturalHeight * REFERENCE_VIEWBOX_H, 1);
    const paths = useMemo(() => {
        return frame.strokes
            .map((stroke) => strokeToSvgPath(stroke, viewBoxW, viewBoxH, {
            size: STROKE_RENDER_SIZE,
        }))
            .filter((d) => typeof d === "string" && d.length > 0);
    }, [frame.strokes, viewBoxW, viewBoxH]);
    // -- Body drag (move) gesture --------------------------------------------
    const onBodyPointerDown = useCallback((e) => {
        if (!selection)
            return;
        if (e.pointerType === "mouse" && e.button !== 0)
            return;
        const rect = selection.containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0)
            return;
        // Selecting and starting move on the same pointerdown: the
        // user's drag picks up immediately without needing a second
        // press. If the click never moves we still leave the
        // drawing selected (matches Figma's "single click selects").
        if (!selection.isSelected) {
            selection.onSelect(annotation.id);
        }
        const pointerNormX = (e.clientX - rect.left) / rect.width;
        const pointerNormY = (e.clientY - rect.top) / rect.height;
        moveStartRef.current = {
            grabOffsetX: pointerNormX - frame.x,
            grabOffsetY: pointerNormY - frame.y,
        };
        moveDirtyRef.current = false;
        e.currentTarget.setPointerCapture(e.pointerId);
        e.stopPropagation();
    }, [annotation.id, frame.x, frame.y, selection]);
    const onBodyPointerMove = useCallback((e) => {
        const start = moveStartRef.current;
        if (!start || !selection)
            return;
        const rect = selection.containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0)
            return;
        const pointerNormX = (e.clientX - rect.left) / rect.width;
        const pointerNormY = (e.clientY - rect.top) / rect.height;
        moveDirtyRef.current = true;
        selection.onPatch(annotation.id, {
            x: pointerNormX - start.grabOffsetX,
            y: pointerNormY - start.grabOffsetY,
        });
    }, [annotation.id, selection]);
    const onBodyPointerUp = useCallback((e) => {
        if (!moveStartRef.current || !selection)
            return;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        moveStartRef.current = null;
        // Only persist if the pointer actually moved during this
        // gesture. A pure click-to-select has no on-screen change,
        // and skipping the PATCH avoids racing it against a
        // follow-up delete keystroke.
        const dirty = moveDirtyRef.current;
        moveDirtyRef.current = false;
        if (dirty) {
            selection.onSettle(annotation.id);
        }
    }, [annotation.id, selection]);
    // Wrapper transform delegated to `annotationTransformStyle` with
    // `sizing: "container"`. The wrapper is 100% × 100% of the host;
    // `scale(frame.width, frame.height)` shrinks it to the bbox
    // fraction, `translate((x-0.5)*100%, (y-0.5)*100%)` repositions
    // its center from `(0.5, 0.5)` of the host to `(x, y)`, and
    // `rotate(R)` pivots around the bbox center.
    //
    // The helper also writes `--annotation-inverse-scale-x/y` (the
    // reciprocals of the per-axis scale) onto the wrapper. The hit-
    // test stroke compensation (`compensatedHitStrokeWidth`) reads
    // those reciprocals via JS so a small drawing's invisible click-
    // tolerance band stays at a constant screen-pixel size. The vars
    // also stay exposed for the shared selection chrome — same pipe
    // stamps use for their counter-scaled handles.
    //
    // Drawings are the only annotation type with non-uniform scale
    // (a wide squiggle has different scaleX vs scaleY); marks always
    // pass equal X/Y because their gesture stack only emits a uniform
    // `scale`. The helper handles both transparently.
    const wrapperStyle = annotationTransformStyle({
        centerX: frame.x,
        centerY: frame.y,
        rotation: frame.rotation,
        scaleX: frame.width,
        scaleY: frame.height,
        sizing: "container",
    });
    const isInteractive = mode === "expanded" && selection !== undefined;
    const className = [
        "annotation-drawing",
        `annotation-drawing--mode-${mode}`,
        isInteractive && "annotation-drawing--interactive",
        selection?.isSelected && "annotation-drawing--selected",
    ]
        .filter(Boolean)
        .join(" ");
    // Pre-multiply the hit-stroke width so the post-CSS-scale band lands
    // back at ~`STROKE_HIT_PADDING_PX` screen px. `vector-effect:
    // non-scaling-stroke` on the path neutralizes the SVG's internal
    // viewBox→host zoom, but does NOT see the wrapper's CSS scale —
    // hence this compensation. See `compensatedHitStrokeWidth` for
    // the math.
    const hitStrokeWidth = compensatedHitStrokeWidth(frame.width, frame.height);
    // Hover producer for the right-rail author avatars. Hovering a
    // drawing publishes its `(id, ownerId)` to the shared annotation
    // hover store; the avatar component subscribes and dims every
    // avatar whose ownerId doesn't match. Same store the comment
    // highlights / rail notes feed into, so a card carrying both a
    // drawing and a comment uses one source of truth for "who is the
    // user attending to right now".
    //
    // Producer only fires in expanded mode because the wrapper is
    // `pointer-events: none` in regular (stream-card) mode — by design,
    // so that taps on the card pass through the annotations layer to
    // the card's expand handler. Stream cards still benefit from the
    // dim feedback when hovering comments (whose rects opt into pointer
    // events on their own); drawings stay decorative there.
    //
    // `onMouseOver` / `onMouseOut` (not `onMouseEnter` / `Leave`)
    // because the wrapper itself stays `pointer-events: none` when the
    // drawing is unselected — only the inner `<path>` strokes catch
    // events. The `over`/`out` pair bubbles, so the wrapper's listeners
    // still fire when the cursor crosses a path. `relatedTarget` gates
    // the leave so moving from one stroke path to another inside the
    // same drawing doesn't briefly clear the hover slot and re-flash
    // the avatar dim.
    const setHoveredAnnotation = useAnnotationHoverStore((s) => s.setHoveredAnnotation);
    const handleHoverEnter = useCallback(() => {
        if (mode !== "expanded")
            return;
        setHoveredAnnotation({
            id: annotation.id,
            ownerId: annotation.ownerId,
            blockId: annotation.blockId,
        });
    }, [
        annotation.id,
        annotation.ownerId,
        annotation.blockId,
        mode,
        setHoveredAnnotation,
    ]);
    const handleHoverLeave = useCallback((e) => {
        if (mode !== "expanded")
            return;
        const related = e.relatedTarget;
        if (related instanceof Node && e.currentTarget.contains(related)) {
            return;
        }
        setHoveredAnnotation(undefined);
    }, [mode, setHoveredAnnotation]);
    return (_jsx("div", { className: className, style: wrapperStyle, "data-drawing-id": annotation.id, onPointerDown: isInteractive ? onBodyPointerDown : undefined, onPointerMove: isInteractive ? onBodyPointerMove : undefined, onPointerUp: isInteractive ? onBodyPointerUp : undefined, onPointerCancel: isInteractive ? onBodyPointerUp : undefined, onMouseOver: handleHoverEnter, onMouseOut: handleHoverLeave, "aria-hidden": true, children: _jsx("svg", { className: "annotation-drawing__svg", viewBox: `0 0 ${viewBoxW} ${viewBoxH}`, preserveAspectRatio: "none", "aria-hidden": true, children: paths.map((d, i) => (_jsx("path", { d: d, fill: annotation.content.color, 
                // Transparent stroke extends the path's hit region beyond
                // the visible polygon by ~`STROKE_HIT_PADDING_PX / 2` on
                // each side. `vector-effect: non-scaling-stroke` neutralizes
                // the SVG's viewBox→host zoom so the band stays a constant
                // SVG-pixel width; the `compensatedHitStrokeWidth` factor
                // then pre-multiplies for the wrapper's parent CSS scale
                // (which `non-scaling-stroke` does NOT see), so the final
                // screen-px band lands at `STROKE_HIT_PADDING_PX` regardless
                // of bbox size. A tiny drawing keeps a usable hit target;
                // a huge drawing doesn't paint a comically thick band.
                stroke: "transparent", strokeWidth: hitStrokeWidth, vectorEffect: "non-scaling-stroke", 
                // Hit-test gating:
                //   - expanded + interactive — `all` hits anywhere within
                //     the geometric region (fill + transparent stroke band)
                //     so the user can grab the polygon to select / move it.
                //     `visiblePainted` (the default) would only count the
                //     colored fill and miss the transparent tolerance band.
                //   - regular (stream) — `none`, so the path is NOT a hit
                //     target. The whole annotations layer is `pointer-events:
                //     none` by design (the card itself is the drag/expand
                //     target); leaving the SVG path as a hit target was
                //     swallowing card hover + click in the area covered by
                //     a drawing's transparent tolerance band. With `none`
                //     here, hover/click on the drawing falls through to
                //     `.block-view` underneath — the card's hover tint
                //     fires, the pointer cursor reads correctly, and the
                //     click triggers the block-expand handler the same as
                //     a click on any other part of the card.
                pointerEvents: isInteractive ? "all" : "none" }, i))) }) }));
});
